import { useState, useEffect } from 'react';
import { supabaseClient as supabase } from '../lib/supabase/index';

const LAST_READ_KEY = 'reception_last_read_at';
const CONV_KEY_PREFIX = 'chat_conversation_';
const UNREAD_EVENT = 'chatbot_unread_changed';

export function markReceptionMessagesAsRead(): void {
  const now = new Date().toISOString();
  localStorage.setItem(LAST_READ_KEY, now);
  window.dispatchEvent(new CustomEvent(UNREAD_EVENT, { detail: 0 }));
}

function getLastReadAt(): string | null {
  return localStorage.getItem(LAST_READ_KEY);
}

function getConversationId(hotelId: string): string | null {
  return (
    localStorage.getItem(`${CONV_KEY_PREFIX}${hotelId}`) ||
    sessionStorage.getItem(`${CONV_KEY_PREFIX}${hotelId}`)
  );
}

export function useUnreadReceptionMessages(hotelId: string): number {
  const [count, setCount] = useState<number>(0);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    hotelId ? getConversationId(hotelId) : null
  );

  useEffect(() => {
    if (hotelId) {
      setConversationId(getConversationId(hotelId));
    }
  }, [hotelId]);

  useEffect(() => {
    const onConvReady = (e: Event) => {
      const detail = (e as CustomEvent<{ hotelId: string; conversationId: string }>).detail;
      if (detail.hotelId === hotelId) {
        setConversationId(detail.conversationId);
      }
    };
    window.addEventListener('chatbot_conversation_ready', onConvReady);
    return () => window.removeEventListener('chatbot_conversation_ready', onConvReady);
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId || !conversationId) {
      setCount(0);
      return;
    }

    const fetchUnread = async () => {
      const lastReadAt = getLastReadAt();

      let query = supabase
        .from('chatbot_messages')
        .select('id', { count: 'exact', head: true })
        .eq('conversation_id', conversationId)
        .eq('role', 'reception');

      if (lastReadAt) {
        query = query.gt('created_at', lastReadAt);
      }

      const { count: unreadCount, error } = await query;
      if (error) {
        console.error('[unread] query error:', error);
      }
      const newCount = unreadCount ?? 0;
      setCount(newCount);
      window.dispatchEvent(new CustomEvent(UNREAD_EVENT, { detail: newCount }));
    };

    fetchUnread();

    const channel = supabase
      .channel(`unread-chatbot-conv-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chatbot_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          const msg = payload.new as { role: string; created_at: string };
          if (msg.role === 'reception') {
            const lastReadAt = getLastReadAt();
            if (!lastReadAt || new Date(msg.created_at) > new Date(lastReadAt)) {
              setCount(prev => {
                const next = prev + 1;
                window.dispatchEvent(new CustomEvent(UNREAD_EVENT, { detail: next }));
                return next;
              });
            }
          }
        }
      )
      .subscribe();

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      setCount(detail);
    };
    window.addEventListener(UNREAD_EVENT, handler);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener(UNREAD_EVENT, handler);
    };
  }, [hotelId, conversationId]);

  return count;
}
